import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { 
  TrendingUp, 
  PieChart, 
  BarChart3, 
  Zap, 
  Shield, 
  Target,
  AlertTriangle,
  ChevronDown,
  Plus,
  Trash2,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const PortfolioForecastingPage = () => {
  const [portfolioItems, setPortfolioItems] = useState([
    { id: 1, name: 'Large Cap Equity', allocation: 30, expectedReturn: 12, risk: 'Medium' },
    { id: 2, name: 'Mid Cap Equity', allocation: 20, expectedReturn: 15, risk: 'High' },
    { id: 3, name: 'Small Cap Equity', allocation: 10, expectedReturn: 18, risk: 'Very High' },
    { id: 4, name: 'Debt Funds', allocation: 25, expectedReturn: 7, risk: 'Low' },
    { id: 5, name: 'Gold/Commodities', allocation: 10, expectedReturn: 8, risk: 'Medium' },
    { id: 6, name: 'International Equity', allocation: 5, expectedReturn: 10, risk: 'High' },
  ]);
  
  const [investmentAmount, setInvestmentAmount] = useState(5000000);
  const [investmentHorizon, setInvestmentHorizon] = useState(10);
  const [monthlyAddition, setMonthlyAddition] = useState(50000);
  const [inflationRate, setInflationRate] = useState(6);
  const [forecast, setForecast] = useState(null);
  const [scenarioAnalysis, setScenarioAnalysis] = useState(null);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [newAsset, setNewAsset] = useState({ name: '', allocation: 0, expectedReturn: 10, risk: 'Medium' });
  const [activeTab, setActiveTab] = useState('forecast');

  const assetTemplates = [
    { name: 'Large Cap Equity', expectedReturn: 12, risk: 'Medium' },
    { name: 'Mid Cap Equity', expectedReturn: 15, risk: 'High' },
    { name: 'Small Cap Equity', expectedReturn: 18, risk: 'Very High' },
    { name: 'Flexi Cap Equity', expectedReturn: 14, risk: 'High' },
    { name: 'Index Funds (Nifty 50)', expectedReturn: 11, risk: 'Medium' },
    { name: 'Debt - Liquid', expectedReturn: 5, risk: 'Very Low' },
    { name: 'Debt - Short Term', expectedReturn: 6.5, risk: 'Low' },
    { name: 'Debt - Corporate Bond', expectedReturn: 7.5, risk: 'Low' },
    { name: 'Debt - Gilt', expectedReturn: 7, risk: 'Low' },
    { name: 'Hybrid - Aggressive', expectedReturn: 10, risk: 'Medium' },
    { name: 'Hybrid - Balanced', expectedReturn: 9, risk: 'Medium' },
    { name: 'Gold ETF', expectedReturn: 8, risk: 'Medium' },
    { name: 'International Equity', expectedReturn: 10, risk: 'High' },
    { name: 'REIT', expectedReturn: 9, risk: 'Medium' },
    { name: 'Fixed Deposit', expectedReturn: 6, risk: 'Very Low' },
    { name: 'PPF', expectedReturn: 7.1, risk: 'Very Low' },
  ];

  const riskColors = {
    'Very Low': 'text-green-400 bg-green-500/20',
    'Low': 'text-emerald-400 bg-emerald-500/20',
    'Medium': 'text-yellow-400 bg-yellow-500/20',
    'High': 'text-orange-400 bg-orange-500/20',
    'Very High': 'text-red-400 bg-red-500/20',
  };

  const calculateForecast = useCallback(() => {
    const totalAllocation = portfolioItems.reduce((sum, item) => sum + item.allocation, 0);
    if (totalAllocation !== 100) return;

    // Calculate weighted average return
    const weightedReturn = portfolioItems.reduce((sum, item) => {
      return sum + (item.allocation / 100) * item.expectedReturn;
    }, 0);

    // Calculate portfolio risk (simplified standard deviation)
    const riskWeights = { 'Very Low': 3, 'Low': 6, 'Medium': 12, 'High': 18, 'Very High': 25 };
    const weightedRisk = portfolioItems.reduce((sum, item) => {
      return sum + (item.allocation / 100) * riskWeights[item.risk];
    }, 0);

    // Year by year projection
    const yearlyProjection = [];
    let currentValue = investmentAmount;
    const monthlyRate = weightedReturn / 100 / 12;

    for (let year = 1; year <= investmentHorizon; year++) {
      // Calculate with monthly compounding and additions
      for (let month = 1; month <= 12; month++) {
        currentValue = currentValue * (1 + monthlyRate) + monthlyAddition;
      }
      
      const inflationAdjusted = currentValue / Math.pow(1 + inflationRate / 100, year);
      
      yearlyProjection.push({
        year,
        nominalValue: Math.round(currentValue),
        realValue: Math.round(inflationAdjusted),
        totalInvested: investmentAmount + (monthlyAddition * 12 * year),
      });
    }

    // Final values
    const finalNominal = yearlyProjection[yearlyProjection.length - 1].nominalValue;
    const finalReal = yearlyProjection[yearlyProjection.length - 1].realValue;
    const totalInvested = investmentAmount + (monthlyAddition * 12 * investmentHorizon);
    const absoluteReturns = finalNominal - totalInvested;
    const cagr = (Math.pow(finalNominal / investmentAmount, 1 / investmentHorizon) - 1) * 100;

    setForecast({
      weightedReturn: weightedReturn.toFixed(2),
      weightedRisk: weightedRisk.toFixed(1),
      finalNominal,
      finalReal,
      totalInvested,
      absoluteReturns,
      cagr: cagr.toFixed(2),
      yearlyProjection,
    });

    // Scenario Analysis
    const scenarios = {
      bull: {
        name: 'Bull Market',
        returnMultiplier: 1.3,
        probability: 25,
        icon: ArrowUpRight,
        color: 'green',
      },
      base: {
        name: 'Base Case',
        returnMultiplier: 1.0,
        probability: 50,
        icon: TrendingUp,
        color: 'blue',
      },
      bear: {
        name: 'Bear Market',
        returnMultiplier: 0.6,
        probability: 25,
        icon: ArrowDownRight,
        color: 'red',
      },
    };

    const scenarioResults = {};
    Object.entries(scenarios).forEach(([key, scenario]) => {
      let value = investmentAmount;
      const adjustedReturn = weightedReturn * scenario.returnMultiplier;
      const monthlyR = adjustedReturn / 100 / 12;

      for (let year = 1; year <= investmentHorizon; year++) {
        for (let month = 1; month <= 12; month++) {
          value = value * (1 + monthlyR) + monthlyAddition;
        }
      }

      scenarioResults[key] = {
        ...scenario,
        finalValue: Math.round(value),
        adjustedReturn: adjustedReturn.toFixed(1),
      };
    });

    setScenarioAnalysis(scenarioResults);
  }, [portfolioItems, investmentAmount, investmentHorizon, monthlyAddition, inflationRate]);

  useEffect(() => {
    calculateForecast();
  }, [calculateForecast]);

  const totalAllocation = portfolioItems.reduce((sum, item) => sum + item.allocation, 0);

  const handleAllocationChange = (id, value) => {
    setPortfolioItems(items =>
      items.map(item =>
        item.id === id ? { ...item, allocation: Math.max(0, Math.min(100, parseInt(value) || 0)) } : item
      )
    );
  };

  const handleReturnChange = (id, value) => {
    setPortfolioItems(items =>
      items.map(item =>
        item.id === id ? { ...item, expectedReturn: Math.max(0, parseFloat(value) || 0) } : item
      )
    );
  };

  const removeAsset = (id) => {
    setPortfolioItems(items => items.filter(item => item.id !== id));
  };

  const addAsset = () => {
    if (newAsset.name && newAsset.allocation > 0) {
      setPortfolioItems([
        ...portfolioItems,
        { ...newAsset, id: Date.now() }
      ]);
      setNewAsset({ name: '', allocation: 0, expectedReturn: 10, risk: 'Medium' });
      setShowAddAsset(false);
    }
  };

  const selectTemplate = (template) => {
    setNewAsset({
      ...newAsset,
      name: template.name,
      expectedReturn: template.expectedReturn,
      risk: template.risk,
    });
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full border border-purple-500/30 mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">Advanced Portfolio Analytics</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Portfolio Forecasting
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              AI-powered portfolio analysis with multi-scenario forecasting, risk assessment, and inflation-adjusted projections
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Panel - Portfolio Builder */}
            <div className="lg:col-span-2 space-y-6">
              {/* Investment Parameters */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Investment Parameters
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-white/70 text-sm mb-2 block">Initial Investment</label>
                    <input
                      type="number"
                      value={investmentAmount}
                      onChange={(e) => setInvestmentAmount(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                    />
                    <span className="text-white/50 text-xs mt-1 block">{formatCurrency(investmentAmount)}</span>
                  </div>
                  <div>
                    <label className="text-white/70 text-sm mb-2 block">Monthly SIP</label>
                    <input
                      type="number"
                      value={monthlyAddition}
                      onChange={(e) => setMonthlyAddition(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                    />
                    <span className="text-white/50 text-xs mt-1 block">{formatCurrency(monthlyAddition)}/month</span>
                  </div>
                  <div>
                    <label className="text-white/70 text-sm mb-2 block">Time Horizon (Years)</label>
                    <input
                      type="number"
                      value={investmentHorizon}
                      onChange={(e) => setInvestmentHorizon(parseInt(e.target.value) || 1)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-white/70 text-sm mb-2 block">Expected Inflation (%)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={inflationRate}
                      onChange={(e) => setInflationRate(parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Asset Allocation */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-blue-400" />
                    Asset Allocation
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      totalAllocation === 100 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {totalAllocation}% allocated
                    </span>
                    <button
                      onClick={() => setShowAddAsset(true)}
                      className="flex items-center gap-1 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm hover:bg-purple-500/30 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Asset
                    </button>
                  </div>
                </div>

                {totalAllocation !== 100 && (
                  <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-300 text-sm">
                      Total allocation must equal 100%. Currently: {totalAllocation}%
                    </span>
                  </div>
                )}

                <div className="space-y-3">
                  {portfolioItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white/5 rounded-2xl p-4 border border-white/10 hover:border-white/20 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{
                            background: `hsl(${item.id * 45}, 70%, 50%)`
                          }} />
                          <span className="text-white font-medium">{item.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${riskColors[item.risk]}`}>
                            {item.risk}
                          </span>
                        </div>
                        <button
                          onClick={() => removeAsset(item.id)}
                          className="p-1.5 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-white/50 text-xs mb-1 block">Allocation (%)</label>
                          <input
                            type="number"
                            value={item.allocation}
                            onChange={(e) => handleAllocationChange(item.id, e.target.value)}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                        <div>
                          <label className="text-white/50 text-xs mb-1 block">Expected Return (%)</label>
                          <input
                            type="number"
                            step="0.5"
                            value={item.expectedReturn}
                            onChange={(e) => handleReturnChange(item.id, e.target.value)}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                          />
                        </div>
                      </div>
                      {/* Allocation Bar */}
                      <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${item.allocation}%`,
                            background: `hsl(${item.id * 45}, 70%, 50%)`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Asset Modal */}
                {showAddAsset && (
                  <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-3xl p-6 max-w-lg w-full border border-white/10 max-h-[80vh] overflow-y-auto">
                      <h3 className="text-xl font-bold text-white mb-4">Add Asset Class</h3>
                      
                      <div className="mb-4">
                        <label className="text-white/70 text-sm mb-2 block">Quick Select Template</label>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {assetTemplates.map((template, idx) => (
                            <button
                              key={idx}
                              onClick={() => selectTemplate(template)}
                              className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                newAsset.name === template.name
                                  ? 'bg-purple-500/30 border border-purple-500 text-white'
                                  : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10'
                              }`}
                            >
                              {template.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="text-white/70 text-sm mb-2 block">Asset Name</label>
                          <input
                            type="text"
                            value={newAsset.name}
                            onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500"
                            placeholder="e.g., Large Cap Equity"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-white/70 text-sm mb-2 block">Allocation (%)</label>
                            <input
                              type="number"
                              value={newAsset.allocation}
                              onChange={(e) => setNewAsset({ ...newAsset, allocation: parseInt(e.target.value) || 0 })}
                              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500"
                            />
                          </div>
                          <div>
                            <label className="text-white/70 text-sm mb-2 block">Expected Return (%)</label>
                            <input
                              type="number"
                              step="0.5"
                              value={newAsset.expectedReturn}
                              onChange={(e) => setNewAsset({ ...newAsset, expectedReturn: parseFloat(e.target.value) || 0 })}
                              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-white/70 text-sm mb-2 block">Risk Level</label>
                          <div className="grid grid-cols-5 gap-2">
                            {Object.keys(riskColors).map((risk) => (
                              <button
                                key={risk}
                                onClick={() => setNewAsset({ ...newAsset, risk })}
                                className={`px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                                  newAsset.risk === risk
                                    ? riskColors[risk]
                                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                                }`}
                              >
                                {risk}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <button
                          onClick={() => setShowAddAsset(false)}
                          className="flex-1 px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={addAsset}
                          className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors"
                        >
                          Add Asset
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Results */}
            <div className="space-y-6">
              {/* Portfolio Summary */}
              {forecast && (
                <>
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-6 border border-purple-500/30">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-400" />
                      Portfolio Summary
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Weighted Return</span>
                        <span className="text-2xl font-bold text-green-400">{forecast.weightedReturn}%</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Portfolio Risk</span>
                        <span className={`text-lg font-semibold ${
                          forecast.weightedRisk < 10 ? 'text-green-400' :
                          forecast.weightedRisk < 15 ? 'text-yellow-400' : 'text-red-400'
                        }`}>{forecast.weightedRisk}%</span>
                      </div>
                      <div className="h-px bg-white/10" />
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Total Invested</span>
                        <span className="text-white font-semibold">{formatCurrency(forecast.totalInvested)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Projected Value */}
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-xl rounded-3xl p-6 border border-green-500/30">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      {investmentHorizon} Year Projection
                    </h3>
                    <div className="text-center mb-4">
                      <p className="text-white/50 text-sm">Projected Value</p>
                      <p className="text-4xl font-bold text-green-400">{formatCurrency(forecast.finalNominal)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/5 rounded-xl p-3 text-center">
                        <p className="text-white/50 text-xs mb-1">Inflation Adjusted</p>
                        <p className="text-lg font-bold text-white">{formatCurrency(forecast.finalReal)}</p>
                      </div>
                      <div className="bg-white/5 rounded-xl p-3 text-center">
                        <p className="text-white/50 text-xs mb-1">CAGR</p>
                        <p className="text-lg font-bold text-green-400">{forecast.cagr}%</p>
                      </div>
                    </div>
                    <div className="mt-4 bg-white/5 rounded-xl p-3 text-center">
                      <p className="text-white/50 text-xs mb-1">Absolute Returns</p>
                      <p className="text-2xl font-bold text-green-400">+{formatCurrency(forecast.absoluteReturns)}</p>
                    </div>
                  </div>

                  {/* Scenario Analysis */}
                  {scenarioAnalysis && (
                    <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-blue-400" />
                        Scenario Analysis
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(scenarioAnalysis).map(([key, scenario]) => {
                          const Icon = scenario.icon;
                          const colors = {
                            green: 'from-green-500/20 to-green-500/10 border-green-500/30 text-green-400',
                            blue: 'from-blue-500/20 to-blue-500/10 border-blue-500/30 text-blue-400',
                            red: 'from-red-500/20 to-red-500/10 border-red-500/30 text-red-400',
                          };
                          return (
                            <div
                              key={key}
                              className={`bg-gradient-to-r ${colors[scenario.color]} rounded-xl p-4 border`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  <span className="font-medium text-white">{scenario.name}</span>
                                </div>
                                <span className="text-xs text-white/50">{scenario.probability}% probability</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-white/70">Return: {scenario.adjustedReturn}%</span>
                                <span className="font-bold">{formatCurrency(scenario.finalValue)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Year-wise Breakdown */}
                  <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-cyan-400" />
                      Growth Trajectory
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
                      {forecast.yearlyProjection.map((year, idx) => (
                        <div
                          key={year.year}
                          className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                        >
                          <span className="text-white/70 text-sm">Year {year.year}</span>
                          <div className="text-right">
                            <p className="text-white font-medium">{formatCurrency(year.nominalValue)}</p>
                            <p className="text-white/50 text-xs">Real: {formatCurrency(year.realValue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 font-semibold mb-2">Important Disclaimer</p>
                <p className="text-white/50 text-sm">
                  This portfolio forecasting tool is for educational and illustrative purposes only. Actual investment returns may vary significantly based on market conditions, fund selection, and economic factors. Past performance does not guarantee future results. The projections shown are estimates based on historical averages and should not be considered as investment advice. Please consult a certified financial advisor before making any investment decisions. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PortfolioForecastingPage;
